import React, { useState, useEffect } from 'react';
import { getBackendURL } from '../../utils/backendURL';
import axios from 'axios';
import { 
  Headphones, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  Clock,
  AlertCircle,
  CheckCircle,
  Calendar,
  User,
  Mail,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Bug,
  Lightbulb,
  CreditCard,
  HelpCircle,
  Zap
} from 'lucide-react';

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [stats, setStats] = useState({});
  const ticketsPerPage = 10;

  const statuses = {
    open: { label: 'Open', color: 'bg-red-100 text-red-800', icon: AlertCircle },
    in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    waiting_for_user: { label: 'Waiting for User', color: 'bg-blue-100 text-blue-800', icon: User },
    resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    closed: { label: 'Closed', color: 'bg-gray-100 text-gray-800', icon: CheckCircle }
  };

  const priorities = {
    low: { label: 'Low', color: 'text-green-600' },
    medium: { label: 'Medium', color: 'text-yellow-600' },
    high: { label: 'High', color: 'text-orange-600' },
    urgent: { label: 'Urgent', color: 'text-red-600' }
  };

  const categories = {
    bug_report: { label: 'Bug Report', icon: Bug, color: 'text-red-600' },
    feature_request: { label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-600' },
    api_issue: { label: 'API Issue', icon: Zap, color: 'text-blue-600' },
    flow_testing_issue: { label: 'Flow Testing Issue', icon: Zap, color: 'text-purple-600' },
    billing_question: { label: 'Billing Question', icon: CreditCard, color: 'text-green-600' },
    account_issue: { label: 'Account Issue', icon: User, color: 'text-indigo-600' },
    general_support: { label: 'General Support', icon: Headphones, color: 'text-gray-600' },
    other: { label: 'Other', icon: HelpCircle, color: 'text-gray-500' }
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [statusFilter, priorityFilter, categoryFilter, currentPage]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      params.append('skip', (currentPage - 1) * ticketsPerPage);
      params.append('limit', ticketsPerPage);

      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${getBackendURL()}/api/ops/support/tickets?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      setError('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${getBackendURL()}/api/ops/support/tickets/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch ticket stats:', error);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${getBackendURL()}/api/ops/support/tickets/${ticketId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSelectedTicket(response.data);
      setShowTicketModal(true);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      alert('Failed to load ticket details');
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${getBackendURL()}/api/ops/support/tickets/${ticketId}`,
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Refresh tickets list
      await fetchTickets();
      await fetchStats();
      
      alert(`Ticket status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update ticket status:', error);
      alert('Failed to update ticket status');
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${getBackendURL()}/api/ops/support/tickets/${ticketId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Refresh tickets list
      await fetchTickets();
      await fetchStats();
      
      alert('Ticket deleted successfully');
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      alert('Failed to delete ticket');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TicketDetailModal = ({ ticket, onClose }) => {
    const [updating, setUpdating] = useState(false);

    const handleStatusChange = async (newStatus) => {
      setUpdating(true);
      try {
        await updateTicketStatus(ticket.id, newStatus);
        setSelectedTicket({ ...ticket, status: newStatus });
      } catch (error) {
        console.error('Failed to update status:', error);
      } finally {
        setUpdating(false);
      }
    };

    if (!ticket) return null;

    const StatusIcon = statuses[ticket.status]?.icon || AlertCircle;
    const CategoryIcon = categories[ticket.category]?.icon || HelpCircle;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center space-x-3">
              <Headphones className="h-6 w-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Ticket #{ticket.ticket_number}</h2>
                <p className="text-sm text-gray-500">{ticket.subject}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Status and Priority */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <StatusIcon className="h-5 w-5" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statuses[ticket.status]?.color}`}>
                  {statuses[ticket.status]?.label}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`font-medium ${priorities[ticket.priority]?.color}`}>
                  {priorities[ticket.priority]?.label} Priority
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <CategoryIcon className={`h-4 w-4 ${categories[ticket.category]?.color}`} />
                <span className="text-sm text-gray-600">{categories[ticket.category]?.label}</span>
              </div>
            </div>

            {/* User Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Name:</span> {ticket.user_name || 'Anonymous'}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {ticket.user_email}
                </div>
                <div>
                  <span className="font-medium">Submitted:</span> {formatDate(ticket.created_at)}
                </div>
                <div>
                  <span className="font-medium">Last Updated:</span> {formatDate(ticket.updated_at)}
                </div>
              </div>
            </div>

            {/* Ticket Description */}
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Description</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap text-sm text-gray-700">{ticket.description}</p>
              </div>
            </div>

            {/* Admin Notes */}
            {ticket.admin_notes && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Admin Notes</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="whitespace-pre-wrap text-sm text-blue-700">{ticket.admin_notes}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-t pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(statuses).map(([status, config]) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={updating || ticket.status === status}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      ticket.status === status
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={fetchTickets}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600">Manage customer support requests and issues</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={fetchTickets}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total_tickets || 0}</p>
            </div>
            <Headphones className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Open Tickets</p>
              <p className="text-2xl font-bold text-red-900">{stats.open_tickets || 0}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Resolved</p>
              <p className="text-2xl font-bold text-green-900">
                {stats.status_distribution?.resolved || 0}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="admin-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600">In Progress</p>
              <p className="text-2xl font-bold text-yellow-900">
                {stats.status_distribution?.in_progress || 0}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Object.entries(statuses).map(([key, status]) => (
              <option key={key} value={key}>{status.label}</option>
            ))}
          </select>

          {/* Priority Filter */}
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">All Priorities</option>
            {Object.entries(priorities).map(([key, priority]) => (
              <option key={key} value={key}>{priority.label}</option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {Object.entries(categories).map(([key, category]) => (
              <option key={key} value={key}>{category.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tickets.length > 0 ? tickets.map((ticket) => {
                const StatusIcon = statuses[ticket.status]?.icon || AlertCircle;
                const CategoryIcon = categories[ticket.category]?.icon || HelpCircle;
                
                return (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          #{ticket.ticket_number}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {ticket.subject}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-medium">
                            {(ticket.user_name || ticket.user_email)?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {ticket.user_name || 'Anonymous'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {ticket.user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <CategoryIcon className={`h-4 w-4 ${categories[ticket.category]?.color}`} />
                        <span className="text-sm text-gray-900">{categories[ticket.category]?.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${priorities[ticket.priority]?.color}`}>
                        {priorities[ticket.priority]?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statuses[ticket.status]?.color}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statuses[ticket.status]?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(ticket.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => fetchTicketDetails(ticket.id)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => updateTicketStatus(ticket.id, 
                          ticket.status === 'open' ? 'in_progress' : 
                          ticket.status === 'in_progress' ? 'resolved' : 'open'
                        )}
                        className="text-green-600 hover:text-green-900 mr-2"
                        title="Update Status"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => deleteTicket(ticket.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Ticket"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <Headphones className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No support tickets found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {tickets.length > 0 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * ticketsPerPage + 1} to {Math.min(currentPage * ticketsPerPage, tickets.length)} of {tickets.length} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md text-sm">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={tickets.length < ticketsPerPage}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => {
            setShowTicketModal(false);
            setSelectedTicket(null);
          }}
        />
      )}
    </div>
  );
};

export default Support;